import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  GiftIcon,
  DocumentTextIcon,
  CakeIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';
import { useFamilyStore } from '../../store/familyStore';
import MemberAvatar from '../../components/common/MemberAvatar';

// Ring icon for anniversaries
const RingIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="6" />
    <path d="M12 2v4" />
    <path d="M9 3l3 3 3-3" />
  </svg>
);

const specialDateIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  birthday: CakeIcon,
  nameday: CalendarIcon,
  anniversary: RingIcon,
  wedding: RingIcon,
  other: SparklesIcon,
};

interface MemberDetails {
  member: {
    id: string;
    user_id: string | null;
    first_name: string | null;
    last_name: string | null;
    nickname: string | null;
    avatar_url: string | null;
    role: string;
    is_managed: boolean;
    color: string | null;
  };
  special_dates: Array<{
    id: string;
    date_type: string;
    date: string;
    name: string | null;
    is_recurring: boolean;
  }>;
  tasks: Array<{
    id: string;
    board_id: string;
    board_name: string;
    title: string;
    description: string | null;
    status: string;
    priority: string;
    due_date: string | null;
    created_at: string;
  }>;
  wishlists: Array<{
    id: string;
    name: string;
    description: string | null;
    occasion: string | null;
    gift_count: number;
    gifts: Array<{
      id: string;
      name: string;
      description: string | null;
      url: string | null;
      image_url: string | null;
      price: number | null;
      priority: string;
      status: string;
    }>;
  }>;
  documents: Array<{
    id: string;
    filename: string;
    original_filename: string;
    title: string | null;
    description: string | null;
    mime_type: string;
    file_size: number;
    created_at: string;
  }>;
  events: Array<{
    id: string;
    name: string;
    description: string | null;
    start_datetime: string;
    end_datetime: string;
    all_day: boolean;
  }>;
}

export default function MemberDetail() {
  const { id: familyId, memberId } = useParams<{ id: string; memberId: string }>();
  const navigate = useNavigate();
  const { currentFamily } = useFamilyStore();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['special_dates']));

  const { data: memberDetails, isLoading } = useQuery({
    queryKey: ['memberDetails', familyId, memberId],
    queryFn: async () => {
      const response = await api.get(`/families/${familyId}/members/${memberId}/details`);
      return response.data as MemberDetails;
    },
    enabled: !!familyId && !!memberId,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="page-content flex items-center justify-center min-h-[50vh]">
        <div className="w-10 h-10 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!memberDetails) {
    return (
      <div className="page-content">
        <div className="card text-center py-16">
          <h2 className="text-xl font-bold text-white mb-2">Member not found</h2>
          <button onClick={() => navigate(`/families/${familyId}`)} className="btn-primary mt-4">
            Back to Family
          </button>
        </div>
      </div>
    );
  }

  const { member, special_dates, tasks, wishlists, documents, events } = memberDetails;
  const memberName = member.nickname || `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown';

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(`/families/${familyId}`)} className="btn-icon">
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-4 flex-1">
          <MemberAvatar
            avatarUrl={member.avatar_url}
            name={memberName}
            color={member.color}
            role={member.role}
            size="lg"
          />
          <div>
            <h1 className="text-2xl font-bold text-white">{memberName}</h1>
            <p className="text-surface-400 text-sm">
              {member.is_managed ? 'Managed Member' : 'Family Member'} • {member.role}
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable content with collapsible sections */}
      <div className="space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto">
        {/* Special Dates Section */}
        <CollapsibleSection
          id="special_dates"
          title="Important Dates"
          icon={CakeIcon}
          count={special_dates.length}
          isExpanded={expandedSections.has('special_dates')}
          onToggle={() => toggleSection('special_dates')}
        >
          {special_dates.length === 0 ? (
            <p className="text-surface-400 text-sm py-2">No special dates</p>
          ) : (
            <div className="space-y-2">
              {special_dates.map((date) => {
                const DateIcon = specialDateIcons[date.date_type] || SparklesIcon;
                const dateObj = new Date(date.date);
                return (
                  <div key={date.id} className="flex items-center gap-3 p-2 rounded-lg bg-surface-800">
                    <DateIcon className="w-5 h-5 text-primary-400" />
                    <div className="flex-1">
                      <div className="text-white font-medium">
                        {date.name || date.date_type}
                      </div>
                      <div className="text-sm text-surface-400">
                        {dateObj.toLocaleDateString(undefined, { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                        {date.is_recurring && ' • Recurring'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CollapsibleSection>

        {/* Tasks Section */}
        <CollapsibleSection
          id="tasks"
          title="Tasks"
          icon={ClipboardDocumentListIcon}
          count={tasks.length}
          isExpanded={expandedSections.has('tasks')}
          onToggle={() => toggleSection('tasks')}
        >
          {tasks.length === 0 ? (
            <p className="text-surface-400 text-sm py-2">No tasks assigned</p>
          ) : (
            <div className="space-y-2">
              {tasks.map((task) => (
                <div key={task.id} className="p-3 rounded-lg bg-surface-800">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="text-white font-medium">{task.title}</div>
                      {task.description && (
                        <div className="text-sm text-surface-400 mt-1">{task.description}</div>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-surface-500">
                        <span>Board: {task.board_name}</span>
                        <span className={`px-2 py-0.5 rounded ${
                          task.status === 'done' ? 'bg-emerald-500/20 text-emerald-400' :
                          task.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-surface-700 text-surface-300'
                        }`}>
                          {task.status}
                        </span>
                        <span className={`px-2 py-0.5 rounded ${
                          task.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                          task.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                          task.priority === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-surface-700 text-surface-300'
                        }`}>
                          {task.priority}
                        </span>
                        {task.due_date && (
                          <span>{new Date(task.due_date).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* Wishlists Section */}
        <CollapsibleSection
          id="wishlists"
          title="Wishlists"
          icon={GiftIcon}
          count={wishlists.length}
          isExpanded={expandedSections.has('wishlists')}
          onToggle={() => toggleSection('wishlists')}
        >
          {wishlists.length === 0 ? (
            <p className="text-surface-400 text-sm py-2">No wishlists</p>
          ) : (
            <div className="space-y-3">
              {wishlists.map((list) => (
                <div key={list.id} className="p-3 rounded-lg bg-surface-800">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-white font-medium">{list.name}</div>
                      {list.description && (
                        <div className="text-sm text-surface-400">{list.description}</div>
                      )}
                    </div>
                    <span className="text-xs text-surface-500">{list.gift_count} gifts</span>
                  </div>
                  {list.gifts.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {list.gifts.map((gift) => (
                        <div key={gift.id} className="flex items-center gap-2 p-2 rounded bg-surface-900">
                          {gift.image_url && (
                            <img src={gift.image_url} alt={gift.name} className="w-10 h-10 rounded object-cover" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-white truncate">{gift.name}</div>
                            {gift.price && (
                              <div className="text-xs text-emerald-400">${gift.price.toFixed(2)}</div>
                            )}
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            gift.status === 'purchased' ? 'bg-emerald-500/20 text-emerald-400' :
                            gift.status === 'reserved' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-surface-700 text-surface-300'
                          }`}>
                            {gift.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* Documents Section */}
        <CollapsibleSection
          id="documents"
          title="Documents"
          icon={DocumentTextIcon}
          count={documents.length}
          isExpanded={expandedSections.has('documents')}
          onToggle={() => toggleSection('documents')}
        >
          {documents.length === 0 ? (
            <p className="text-surface-400 text-sm py-2">No documents mentioning this member</p>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => (
                <div key={doc.id} className="flex items-center gap-3 p-2 rounded-lg bg-surface-800">
                  <DocumentTextIcon className="w-4 h-4 text-primary-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium truncate">{doc.title || doc.original_filename}</div>
                    {doc.description && (
                      <div className="text-sm text-surface-400 truncate">{doc.description}</div>
                    )}
                    <div className="text-xs text-surface-500 mt-1">
                      {new Date(doc.created_at).toLocaleDateString()} • {(doc.file_size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* Calendar Events Section */}
        <CollapsibleSection
          id="events"
          title="Calendar Events"
          icon={CalendarIcon}
          count={events.length}
          isExpanded={expandedSections.has('events')}
          onToggle={() => toggleSection('events')}
        >
          {events.length === 0 ? (
            <p className="text-surface-400 text-sm py-2">No calendar events</p>
          ) : (
            <div className="space-y-2">
              {events.map((event) => {
                const startDate = new Date(event.start_datetime);
                const endDate = new Date(event.end_datetime);
                return (
                  <div key={event.id} className="p-3 rounded-lg bg-surface-800">
                    <div className="text-white font-medium">{event.name}</div>
                    {event.description && (
                      <div className="text-sm text-surface-400 mt-1">{event.description}</div>
                    )}
                    <div className="text-xs text-surface-500 mt-2">
                      {event.all_day ? (
                        startDate.toLocaleDateString()
                      ) : (
                        `${startDate.toLocaleString()} - ${endDate.toLocaleString()}`
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CollapsibleSection>
      </div>
    </div>
  );
}

interface CollapsibleSectionProps {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  count: number;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function CollapsibleSection({ id, title, icon: Icon, count, isExpanded, onToggle, children }: CollapsibleSectionProps) {
  return (
    <div className="card !p-0 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-surface-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-primary-400" />
          <span className="font-semibold text-white">{title}</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-surface-700 text-surface-300">
            {count}
          </span>
        </div>
        {isExpanded ? (
          <ChevronDownIcon className="w-5 h-5 text-surface-400" />
        ) : (
          <ChevronRightIcon className="w-5 h-5 text-surface-400" />
        )}
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4 border-t border-surface-700">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

